import { MODALITY_LABELS } from '@/features/quotes/utils/rd-modality.util';
import {
  LEASED_TO_THIRD_PARTIES_CLAUSES,
  QUOTE_NOTES,
  STANDARD_CLAUSES,
} from '@/features/quotes/components/rd-equipamentos/rd-policy-clauses.constants';
import { DESPESA_SALVAMENTO_SLUG } from '@/features/quotes/components/rd-equipamentos/steps/sections/coverage-rows.util';
import { useRdAggregateProducts } from '@/features/quotes/hooks/rd-equipamentos/use-rd-aggregate-products.hook';
import { useRdFranchiseRules } from '@/features/quotes/hooks/rd-equipamentos/use-rd-franchise-rules.hook';
import { franchiseTextBySlug } from '@/features/quotes/utils/rd-franchise.util';
import { centsToBRL } from '@/features/quotes/utils/rd-units.util';
import type {
  RdAggregateProducts,
  RdInstallmentOption,
  RdModalitySlug,
  RdProductType,
  RdProposal,
  RdQuoteFrota,
  RdQuoteFrotaCoverage,
  RdQuoteItem,
  RdQuoteItemCoverage,
  RdQuoteResult,
} from '@/services/quotes/rd-equipamentos/types';
import {
  Accordion,
  Alert,
  DataTable,
  Tag,
  Text,
  type DataTableColumn,
} from '@noorden-insurtech/alba-design-system';
import { ChevronDown } from 'lucide-react';
import { useMemo } from 'react';

type QuoteBreakdownProps = {
  quote: RdQuoteResult;
  proposal: RdProposal;
};

function KpiCard({
  label,
  value,
  badge,
}: {
  label: string;
  value: string;
  badge?: string;
}) {
  return (
    <div className='flex flex-col gap-2 rounded-xl border border-alba-gray-400 p-4'>
      <Text className='text-xs text-alba-gray-500'>{label}</Text>
      <Text className='text-2xl font-bold text-alba-black-500'>{value}</Text>
      {badge ? (
        <Tag.Root size='sm' colorScheme='warning' className='w-fit'>
          <Tag.Label>{badge}</Tag.Label>
        </Tag.Root>
      ) : null}
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className='flex flex-col gap-1'>
      <Text className='text-xs text-alba-gray-500'>{label}</Text>
      <Text className='text-sm font-bold text-alba-black-500'>{value}</Text>
    </div>
  );
}

/**
 * Coluna da franquia, compartilhada pelas duas tabelas de cobertura.
 *
 * `franquias` vem indexado por slug; célula sem regra sai `'—'`, o mesmo
 * travessão que o `planoLabel` já usa neste arquivo. Enquanto o catálogo não
 * chegou (`pendente`), sai VAZIO: exibir `'—'` antes da resposta faria a
 * coluna piscar "sem franquia" e depois preencher, o que lê como dado que
 * mudou.
 *
 * `accessorKey` aponta para o nome da cobertura porque a franquia não é campo
 * da linha — a tabela do DS exige uma chave existente, e a ordenação por ela
 * agrupa as coberturas, que é o comportamento útil aqui.
 */
function franchiseColumn<T extends { name: string }>(
  // eslint-disable-next-line no-unused-vars -- nome do parâmetro documenta o contrato da callback
  slugOf: (coverage: T) => string,
  franquias: Map<string, string>,
  pendente: boolean
): DataTableColumn<T> {
  return {
    accessorKey: 'name',
    id: 'franchise',
    header: 'FRANQUIA / POS',
    size: 320,
    cell: ({ row }) => (
      <Text className='text-sm text-alba-black-500'>
        {pendente ? '' : (franquias.get(slugOf(row.original)) ?? '—')}
      </Text>
    ),
  };
}

/**
 * Rótulo da cobertura com o período indenitário, quando há um.
 *
 * **Este é um dos dois únicos lugares que compõem o rótulo** (o outro é
 * `folheteria_payload._nome_com_periodo`, no motor, para o documento). Em
 * todas as demais camadas o período viaja como DADO — no motor, sufixá-lo
 * cedo demais poluiria também o quadro de franquias, que lê a mesma lista.
 */
function nomeComPeriodo(name: string, months?: number): string {
  if (!months) return name;
  const unidade = months === 1 ? 'mês' : 'meses';
  return `${name} (período indenitário: ${months} ${unidade})`;
}

function buildItemCoverageColumns(
  franquias: Map<string, string>,
  pendente: boolean
): DataTableColumn<RdQuoteItemCoverage>[] {
  return [
    {
      accessorKey: 'name',
      header: 'COBERTURA',
      size: 240,
      cell: ({ row }) => (
        <Text className='text-sm text-alba-black-500'>
          {nomeComPeriodo(row.original.name, row.original.indemnityMonths)}
        </Text>
      ),
    },
    {
      accessorKey: 'lmi',
      header: 'LMI',
      size: 150,
      cell: ({ row }) => (
        <Text className='text-sm text-alba-black-500'>
          {centsToBRL(row.original.lmi)}
        </Text>
      ),
    },
    franchiseColumn<RdQuoteItemCoverage>(
      coverage => coverage.coverageSlug,
      franquias,
      pendente
    ),
    {
      accessorKey: 'premium',
      header: 'PRÊMIO',
      size: 150,
      cell: ({ row }) => (
        <Text className='text-sm font-bold text-alba-black-500'>
          {centsToBRL(row.original.premium)}
        </Text>
      ),
    },
  ];
}

function buildFrotaCoverageColumns(
  franquias: Map<string, string>,
  pendente: boolean
): DataTableColumn<RdQuoteFrotaCoverage>[] {
  return [
    {
      accessorKey: 'name',
      header: 'COBERTURA',
      size: 240,
      cell: ({ row }) => (
        <Text className='text-sm text-alba-black-500'>
          {/* No agregado o prazo é um só, e o motor já o entrega dentro da
              linha da cobertura que o pede — simétrico ao individual. */}
          {nomeComPeriodo(row.original.name, row.original.indemnityMonths)}
        </Text>
      ),
    },
    {
      accessorKey: 'lmi',
      header: 'LMI',
      size: 150,
      cell: ({ row }) => (
        <Text className='text-sm text-alba-black-500'>
          {centsToBRL(row.original.lmi)}
        </Text>
      ),
    },
    // O agregado não tem PRÊMIO por cobertura (o preço vem do Valor em Risco),
    // então a franquia fecha a linha.
    franchiseColumn<RdQuoteFrotaCoverage>(
      coverage => coverage.slug,
      franquias,
      pendente
    ),
  ];
}

function buildInstallmentColumns(): DataTableColumn<RdInstallmentOption>[] {
  return [
    {
      accessorKey: 'n',
      header: 'PARCELAS',
      cell: ({ row }) => (
        <Text className='text-sm font-bold text-alba-black-500'>
          {row.original.n}x
        </Text>
      ),
    },
    {
      accessorKey: 'firstCash',
      header: '1ª À VISTA',
      cell: ({ row }) => (
        <Text className='text-sm text-alba-black-500'>
          {centsToBRL(row.original.firstCash)}
        </Text>
      ),
    },
    {
      accessorKey: 'first30d',
      header: '1ª EM 30 DIAS',
      cell: ({ row }) => (
        <Text className='text-sm text-alba-black-500'>
          {centsToBRL(row.original.first30d)}
        </Text>
      ),
    },
  ];
}

type FranquiaResolver = (
  // eslint-disable-next-line no-unused-vars -- nome do parâmetro documenta o contrato da callback
  modalitySlug: RdModalitySlug | undefined
) => Map<string, string>;

type IndividualBreakdownProps = {
  items: RdQuoteItem[];
  equipmentNameByItemId: Map<string, string>;
  /**
   * Resolvido POR ITEM, não uma vez para a tela toda: a franquia varia por
   * modalidade (Proximidade de Água é 1,5% no Móvel e 1,0% no Estacionário),
   * e uma proposta individual pode ter itens de modalidades diferentes.
   */
  franquiasDaModalidade: FranquiaResolver;
  franquiasPendentes: boolean;
};

/**
 * Fluxo individual: um bloco por item cotado — header (nome do equipamento
 * + `Tag` da modalidade + prêmio do item) seguido da `DataTable` de
 * coberturas. `RdQuoteItem` não carrega `equipmentName`; resolvido por
 * `equipmentNameByItemId` (de `proposal.equipment`).
 */
function IndividualBreakdown({
  items,
  equipmentNameByItemId,
  franquiasDaModalidade,
  franquiasPendentes,
}: IndividualBreakdownProps) {
  return (
    <div className='flex flex-col gap-4'>
      <Text className='text-lg font-bold text-alba-black-500'>
        Detalhamento por equipamento
      </Text>
      {items.map(item => {
        // `||` (não `??`): um `equipmentName` salvo em branco ('') também deve
        // cair no fallback do itemId, mesmo padrão de robustez do
        // `EquipmentCard` (`equipment-card.component.tsx`).
        const equipmentName =
          equipmentNameByItemId.get(item.itemId) || item.itemId;
        // A Despesa de Salvamento é DERIVADA (10% da Básica) e não é uma
        // cobertura que o corretor contrata — listá-la aqui a fazia parecer
        // uma escolha (PO 2026-09-03). Ela continua no cálculo: sai da tabela,
        // não do prêmio, e é por isso que a nota abaixo existe.
        const coberturas = item.coverages.filter(
          coverage => coverage.coverageSlug !== DESPESA_SALVAMENTO_SLUG
        );
        const temSalvamento = coberturas.length !== item.coverages.length;
        const coverageColumns = buildItemCoverageColumns(
          franquiasDaModalidade(item.modalitySlug),
          franquiasPendentes
        );
        return (
          <div
            key={item.itemId}
            className='flex flex-col gap-4 rounded-2xl border border-alba-gray-200/80'
          >
            <div className='flex flex-wrap items-center justify-between gap-3 border-b border-alba-gray-200 px-5 py-4'>
              <div className='flex flex-wrap items-center gap-2'>
                <Text className='text-base font-bold text-alba-black-500'>
                  {equipmentName}
                </Text>
                <Tag.Root size='sm' colorScheme='blue'>
                  <Tag.Label>{MODALITY_LABELS[item.modalitySlug]}</Tag.Label>
                </Tag.Root>
              </div>
              <Text className='text-base font-bold text-alba-black-500'>
                {centsToBRL(item.itemPremium)}
              </Text>
            </div>

            {item.status !== 'ok' ? (
              <div className='px-5'>
                <Alert.Root status='warning'>
                  <Alert.Icon />
                  <Alert.Content>
                    <Alert.Description>
                      {item.reason ||
                        'Item recusado pelo motor de precificação.'}
                    </Alert.Description>
                  </Alert.Content>
                </Alert.Root>
              </div>
            ) : null}

            <div className='flex flex-col gap-2 px-5 pb-5'>
              {/* O seletor de itens por página do design system só
                    oferece [10, 20, 30, 40, 50]; como aqui a página é
                    ÚNICA e do tamanho da lista, nenhum desses valores
                    casava e o controle saía como uma caixa VAZIA ao lado
                    do "1" — com cara de campo quebrado (QA de
                    2026-09-04). Declarar a própria página como única
                    opção faz o controle exibir o número que ele de fato
                    usa. */}
              <DataTable
                columns={coverageColumns}
                data={coberturas}
                showTotal={false}
                totalPages={1}
                pageSize={Math.max(coberturas.length, 1)}
                pageSizeOptions={[Math.max(coberturas.length, 1)]}
              />
              {/* Sem esta nota, a soma das linhas visíveis fica MENOR que o
                  prêmio do item no cabeçalho (a diferença é o prêmio da
                  salvamento) e o corretor procura o erro que não existe. */}
              {temSalvamento ? (
                <Text className='text-xs text-alba-gray-500'>
                  O prêmio do item inclui a cobertura Despesa de Salvamento
                  (derivada, 10% da Básica), não detalhada acima.
                </Text>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

type FrotaBreakdownProps = {
  frota: RdQuoteFrota | undefined;
  coverageColumns: DataTableColumn<RdQuoteFrotaCoverage>[];
  productType: RdProductType;
};

/** Modalidade que cada produto agregado representa, lida do cadastro. */
function modalidadeDoAgregado(
  productType: RdProductType,
  aggregateProducts?: RdAggregateProducts
): RdModalitySlug | undefined {
  if (productType !== 'frota' && productType !== 'lmi_unico') return undefined;
  return aggregateProducts?.[productType]?.modality;
}

/**
 * Fluxo agregado (frota/lmi_unico): quadro-resumo (Valor em Risco, LMI
 * Total, Plano como `nome — descrição`) + coberturas informativas da apólice.
 * Taxa efetiva e bucket LMI/VR NÃO são exibidos — o BFF não os envia mais
 * (spec de 2026-07-19).
 * Título distingue Frota vs. Risco para não vazar "frota" no fluxo LMI
 * Único.
 */
function FrotaBreakdown({
  frota,
  coverageColumns,
  productType,
}: FrotaBreakdownProps) {
  if (!frota) return null;

  const planoLabel = frota.planoName
    ? `${frota.planoName}${frota.planoDescription ? ` — ${frota.planoDescription}` : ''}`
    : '—';

  // O quadro de LMI por cobertura é informativo e, quando o corretor o deixa
  // em branco, todas as linhas chegam com `lmi === 0`. Exibir "R$ 0,00" para
  // cada cobertura lê como limite contratado zerado — ocultamos as linhas
  // sem LMI e, se nenhuma tiver, trocamos a tabela pela nota com o LMI Total.
  //
  // A Despesa de Salvamento sai pelo mesmo motivo do fluxo individual: é
  // derivada, não contratada (PO 2026-09-03). Uma lista só, usada no `data=`
  // e no guard de lista vazia — senão uma frota que só tenha salvamento com
  // LMI cairia no texto alternativo em vez de simplesmente não ter quadro.
  const coveragesWithLmi = frota.coverages.filter(
    coverage => coverage.lmi > 0 && coverage.slug !== DESPESA_SALVAMENTO_SLUG
  );

  return (
    <div className='flex flex-col gap-4'>
      <Text className='text-lg font-bold text-alba-black-500'>
        {productType === 'frota'
          ? 'Detalhamento da frota'
          : 'Detalhamento do risco'}
      </Text>
      <div className='grid grid-cols-1 gap-4 rounded-2xl border border-alba-gray-200/80 p-5 sm:grid-cols-2 lg:grid-cols-3'>
        <SummaryRow
          label='Valor em Risco'
          value={centsToBRL(frota.riskValue)}
        />
        <SummaryRow label='LMI Total' value={centsToBRL(frota.totalLmi)} />
        <SummaryRow label='Plano' value={planoLabel} />
      </div>

      {coveragesWithLmi.length > 0 ? (
        <>
          <DataTable
            columns={coverageColumns}
            data={coveragesWithLmi}
            showTotal={false}
            totalPages={1}
            pageSize={Math.max(coveragesWithLmi.length, 1)}
            pageSizeOptions={[Math.max(coveragesWithLmi.length, 1)]}
          />
        </>
      ) : (
        <Text className='text-sm text-alba-gray-500'>
          LMI por cobertura não detalhada — limite único de{' '}
          {centsToBRL(frota.totalLmi)} para o conjunto de coberturas.
        </Text>
      )}
    </div>
  );
}

/**
 * Breakdown compartilhado da cotação RD Equipamentos (ramo 0171) — usado
 * pelo Step 5 (Cotação) e reaproveitado pelo Step 6 (Emissão, Task 9).
 * Espelha `alba-portal-seguros-demo/web/views/pages/rd/_quote_breakdown.html`:
 * KPIs de prêmio líquido/comissão, detalhamento por item (individual) ou
 * quadro agregado (frota/lmi_unico), parcelamento e cláusulas/observações
 * padrão (`STANDARD_CLAUSES`/`QUOTE_NOTES` — inclui a nota de validade da
 * cotação, que NÃO sai na apólice emitida).
 */
export function QuoteBreakdown({ quote, proposal }: QuoteBreakdownProps) {
  const isFrota =
    quote.productType === 'frota' || quote.productType === 'lmi_unico';

  // `equipmentName` pode chegar em branco do backend (item legado/sem nome
  // persistido) — cai no fallback `equipmentCode` antes do `itemId` que
  // `IndividualBreakdown` já usa como último recurso.
  const equipmentNameByItemId = useMemo(
    () =>
      new Map(
        proposal.equipment.map(item => [
          item.itemId,
          item.equipmentName || item.equipmentCode,
        ])
      ),
    [proposal.equipment]
  );

  const installmentColumns = useMemo(buildInstallmentColumns, []);

  // Franquia/POS na tela de cálculo (pedido do Marcelo, PO 2026-07-20).
  // Desde 2026-09-03 é COLUNA da tabela de coberturas, e não um quadro à
  // parte: quem lê LMI e prêmio quer a franquia da mesma linha, ali.
  const { data: franchiseRules, isPending: isFranchisePending } =
    useRdFranchiseRules();
  // O cadastro é o mesmo para a tela toda, mas a REGRA que vale depende da
  // modalidade — por isso o resolvedor, e não um mapa pronto: um item Móvel e
  // um Estacionário na mesma proposta têm franquias diferentes para a mesma
  // Proximidade de Água.
  const franquiasDaModalidade = useMemo(() => {
    const cache = new Map<string, Map<string, string>>();
    return (modalitySlug: RdModalitySlug | undefined) => {
      const chave = modalitySlug ?? '';
      const pronto = cache.get(chave);
      if (pronto) return pronto;
      const mapa = franchiseTextBySlug(franchiseRules ?? [], modalitySlug);
      cache.set(chave, mapa);
      return mapa;
    };
  }, [franchiseRules]);

  // No agregado não há modalidade por cobertura: vale a que o produto
  // representa (Frota → Móvel, LMI Único → Estacionário), lida do cadastro em
  // vez de repetida aqui como constante.
  const { data: aggregateProducts } = useRdAggregateProducts();
  const frotaCoverageColumns = useMemo(
    () =>
      buildFrotaCoverageColumns(
        franquiasDaModalidade(
          modalidadeDoAgregado(quote.productType, aggregateProducts)
        ),
        isFranchisePending
      ),
    [
      franquiasDaModalidade,
      aggregateProducts,
      quote.productType,
      isFranchisePending,
    ]
  );

  // Soma dos valores dos equipamentos (PO 2026-07-20) — só faz sentido no
  // fluxo individual; o agregado informa o próprio Valor em Risco.
  const totalRiskValue = useMemo(
    () =>
      centsToBRL(
        proposal.equipment.reduce((sum, item) => sum + (item.value || 0), 0)
      ),
    [proposal.equipment]
  );

  // Uma vez que `proposal.installments` está definido, o demo filtra a
  // tabela para exibir apenas a parcela escolhida (`row.n == proposal.installments`);
  // antes disso (params ainda não salvos), mostra todas as opções 1..max.
  const installmentRows = useMemo(() => {
    if (!proposal.installments) return quote.installments;
    return quote.installments.filter(row => row.n === proposal.installments);
  }, [quote.installments, proposal.installments]);

  // G-2b: `aguardando_analise` cobre dois cenários bem diferentes — referral
  // suave (ex.: item acima do teto de aceitação automática, sem `errors[]`
  // bloqueantes) vs erro real de configuração que o motor recusou (ex.:
  // `faixa_invalida` "LMI total acima do Valor em Risco."). Antes, o Portal
  // engolia a `reason` específica e mostrava só a mensagem genérica de
  // análise nos dois casos — agora distingue: com `errors[]`, mostra a(s)
  // `reason(s)` do motor; sem `errors[]`, mantém a mensagem suave de
  // referral.
  // O motor marca cada erro com `blocking`: `true` = configuração recusada,
  // `false` = referral suave (prêmio calculado e válido, só segue para
  // análise). Discriminar por `errors.length` tratava os dois como falha e
  // exibia "Proposta com erro de configuração" para uma cotação que tinha
  // preço — o campo existia no motor mas não atravessava o BFF.
  const blockingErrors = quote.errors.filter(quoteError => quoteError.blocking);
  const softErrors = quote.errors.filter(quoteError => !quoteError.blocking);
  const hasQuoteErrors = blockingErrors.length > 0;

  return (
    <div className='flex flex-col gap-8'>
      {proposal.status === 'aguardando_analise' && hasQuoteErrors ? (
        <Alert.Root status='error'>
          <Alert.Icon />
          <Alert.Content>
            <Alert.Title>Proposta com erro de configuração</Alert.Title>
            <Alert.Description>
              {blockingErrors.length === 1 ? (
                blockingErrors[0].reason
              ) : (
                <ul className='list-disc space-y-1 pl-5'>
                  {blockingErrors.map((quoteError, index) => (
                    <li key={`${quoteError.itemId ?? 'quote'}-${index}`}>
                      {quoteError.reason}
                    </li>
                  ))}
                </ul>
              )}
            </Alert.Description>
          </Alert.Content>
        </Alert.Root>
      ) : proposal.status === 'aguardando_analise' ? (
        <Alert.Root status='warning'>
          <Alert.Icon />
          <Alert.Content>
            <Alert.Description>
              Proposta enviada para análise técnica manual da subscrição.
              {softErrors.length > 0 ? (
                <>
                  {' '}
                  {softErrors.length === 1 ? (
                    softErrors[0].reason
                  ) : (
                    <ul className='mt-1 list-disc space-y-1 pl-5'>
                      {softErrors.map((quoteError, index) => (
                        <li key={`${quoteError.itemId ?? 'quote'}-${index}`}>
                          {quoteError.reason}
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              ) : null}
            </Alert.Description>
          </Alert.Content>
        </Alert.Root>
      ) : null}

      <div
        className={`grid grid-cols-1 gap-6 ${isFrota ? 'lg:grid-cols-2' : 'lg:grid-cols-3'}`}
      >
        <KpiCard
          label='Prêmio líquido'
          value={centsToBRL(quote.totalPremium)}
          badge={quote.minPremiumApplied ? 'prêmio mínimo aplicado' : undefined}
        />
        <KpiCard
          label='Comissão do corretor'
          value={centsToBRL(quote.totalCommission)}
        />
        {/* Soma dos valores dos equipamentos (PO 2026-07-20). O agregado já
            exibe o Valor em Risco informado no quadro da frota/risco. */}
        {!isFrota ? (
          <KpiCard label='Valor em risco total' value={totalRiskValue} />
        ) : null}
      </div>

      {isFrota ? (
        <FrotaBreakdown
          frota={quote.frota}
          coverageColumns={frotaCoverageColumns}
          productType={quote.productType}
        />
      ) : (
        <IndividualBreakdown
          items={quote.items}
          equipmentNameByItemId={equipmentNameByItemId}
          franquiasDaModalidade={franquiasDaModalidade}
          franquiasPendentes={isFranchisePending}
        />
      )}

      <div className='flex flex-col gap-4'>
        <Text className='text-lg font-bold text-alba-black-500'>
          Parcelamento
        </Text>
        <DataTable
          columns={installmentColumns}
          data={installmentRows}
          showTotal={false}
          totalPages={1}
          pageSize={Math.max(installmentRows.length, 1)}
          pageSizeOptions={[Math.max(installmentRows.length, 1)]}
        />
      </div>

      <Accordion.Root multiple collapsible defaultValue={[]}>
        <Accordion.Item
          value='clauses'
          className='rounded-2xl border border-alba-gray-200/80'
        >
          <Accordion.ItemTrigger className='flex w-full items-center justify-between gap-4 rounded-2xl px-5 py-4 text-left'>
            <Text className='text-base font-bold text-alba-black-500'>
              Cláusulas e observações gerais
            </Text>
            <Accordion.ItemIndicator className='transition-transform data-[state=open]:rotate-180'>
              <ChevronDown
                className='h-5 w-5 text-alba-black-400'
                strokeWidth={1.6}
              />
            </Accordion.ItemIndicator>
          </Accordion.ItemTrigger>
          <Accordion.ItemContent className='flex flex-col gap-4 border-t border-alba-gray-200 px-5 pb-5 pt-4'>
            <div className='flex flex-col gap-2'>
              <Text className='text-sm font-bold text-alba-black-500'>
                Cláusulas especiais
              </Text>
              <ul className='list-disc space-y-1 pl-5'>
                {(proposal.leasedToThirdParties
                  ? [...LEASED_TO_THIRD_PARTIES_CLAUSES, ...STANDARD_CLAUSES]
                  : STANDARD_CLAUSES
                ).map(clause => (
                  <li key={clause} className='text-sm text-alba-gray-600'>
                    {clause}
                  </li>
                ))}
              </ul>
            </div>
            <div className='flex flex-col gap-2'>
              <Text className='text-sm font-bold text-alba-black-500'>
                Observações gerais
              </Text>
              <ul className='list-disc space-y-1 pl-5'>
                {QUOTE_NOTES.map(note => (
                  <li key={note} className='text-sm text-alba-gray-600'>
                    {note}
                  </li>
                ))}
              </ul>
            </div>
          </Accordion.ItemContent>
        </Accordion.Item>
      </Accordion.Root>
    </div>
  );
}
